import { useContext, useEffect, useMemo, useState } from 'react';
import {
    alpha,
    Avatar,
    Box,
    Button,
    Chip,
    LinearProgress,
    Link,
    Paper,
    Stack,
    Typography,
    useTheme
} from '@mui/material';
import {
    AlternateEmailRounded,
    BadgeRounded,
    BusinessRounded,
    SupportAgentRounded
} from '@mui/icons-material';
import { AppVarsContext } from '../../context/AppVarsContext';
import { useReportProfile } from '../../context/ReportProfileContext';
import fallbackReportLogo from '../../logo.png';
import { getPersistedReportId } from '../report-id-storage';

const parseJson = (value) => {
    if (!value) return {};
    if (typeof value === 'object') return value;

    try {
        return JSON.parse(value);
    } catch (error) {
        console.error('Unable to parse contact rep branding settings:', error);
        return {};
    }
};

const uniqueSources = (sources = []) => [...new Set(sources.filter(Boolean))];

const BrandImage = ({ sources, alt, sx }) => {
    const imageSources = uniqueSources(sources);
    const sourceKey = imageSources.join('|');
    const [index, setIndex] = useState(0);
    //console.log('BrandImage sources:', imageSources);
    useEffect(() => {
        setIndex(0);
    }, [sourceKey]);

    if (!imageSources.length || index >= imageSources.length) {
        return null;
    }

    return (
        <Box
            component="img"
            src={imageSources[index]}
            alt={alt}
            onError={() => setIndex((currentIndex) => currentIndex + 1)}
            sx={sx}
        />
    );
};

const ContactRep = ({ client: initialClient, accessDenied = false, accessProfile }) => {
    const theme = useTheme();
    const { appVars, appID, appLoading } = useContext(AppVarsContext);
    const { reportProfile: contextProfile } = useReportProfile() || {};
    const reportProfile = accessProfile || contextProfile;
    const [rep, setRep] = useState(null);
    const [clientRecord, setClientRecord] = useState(initialClient || null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (initialClient) {
            setClientRecord(initialClient);
        }
    }, [initialClient]);

    useEffect(() => {
        if (accessDenied) { setLoading(false); return undefined; }
        if (appLoading || !appVars?.api_url) {
            return undefined;
        }

        let active = true;

        const fetchData = async () => {
            setLoading(true);

            try {
                const requestOptions = {
                    method: 'GET',
                    redirect: 'follow'
                };

                const reportId = getPersistedReportId();
                if (!reportId) {
                    throw new Error('Missing report id for contact representative lookup.');
                }

                const tagManagerRes = await fetch(`${appVars.api_url}/tagmanager/${reportId}`);
                const clientResponse = await tagManagerRes.json();

                if (active) {
                    setClientRecord(clientResponse);
                }

                if (clientResponse?.owner) {
                    const publicReportSlug = sessionStorage.getItem('reportSlug') || '';
                    const repEndpoint = publicReportSlug
                        ? `${appVars.api_url}/public/report/representative?cid=${encodeURIComponent(reportId)}`
                        : `${appVars.api_url}/users/g/${clientResponse.owner}`;
                    const repRes = await fetch(repEndpoint, publicReportSlug
                        ? { ...requestOptions, headers: { 'X-Report-Slug': publicReportSlug } }
                        : requestOptions);
                    const repResponse = await repRes.json();

                    if (active) {
                        setRep(Array.isArray(repResponse) ? repResponse[0] : repResponse);
                    }
                }
            } catch (error) {
                console.error('Failed to load contact representative data:', error);
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            active = false;
        };
    }, [appLoading, appVars?.api_url, accessDenied]);

    const clientSettings = useMemo(() => parseJson(reportProfile?.clientSettings), [reportProfile?.clientSettings]);
    const clientName = clientRecord?.name || clientRecord?.clientname || sessionStorage.getItem('clientName') || 'your organization';
    const clientLabel = clientRecord?.clientname || clientName;
    const licenseReference = rep?.pkRecId || getPersistedReportId() || 'Unavailable';
    const repName = rep?.name || 'ReportWerks Support';
    const repEmail = rep?.email || 'support@reportwerks.com';
    const repRole = rep?.name ? 'Account representative' : 'Support team';
    const repPhoto = rep?.pkRecId && appVars?.api_url ? `${appVars.api_url}/imageFiles/${rep.pkRecId}.png` : '';
    const mailToLink = `mailto:${repEmail}?subject=${encodeURIComponent(`License = ${licenseReference}`)}`;
    const reportLogoSources = uniqueSources([
        clientSettings?.reportWorksLogo,
        (appVars?.api_url && appID) ? `${appVars.api_url}/imageFiles/${appID}-logo.png` : null,
        fallbackReportLogo
    ]);
    const clientLogoSources = uniqueSources([
        Number(reportProfile?.clientlogo) === 1 ? reportProfile?.clientlogopath : null,
        (appVars?.api_url && clientRecord?.id) ? `${appVars.api_url}/imageFiles/${clientRecord.id}-logo.png` : null,
        (appVars?.api_url && clientRecord?.client) ? `${appVars.api_url}/imageFiles/${clientRecord.client}-logo.png` : null
    ]);
    const supportSteps = [
        `Email ${repName} and include the license reference below.`,
        'Mention that this report is currently blocked during license verification.',
        'Once the license is updated, the dashboard can be reactivated.'
    ];

    const renderFooter = () => (
        <Box
            component="footer"
            sx={{
                gridColumn: '1 / -1',
                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                px: { xs: 3, md: 4 },
                py: 2.5,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                background: alpha('#ffffff', 0.82)
            }}
        >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Link href="https://www.reportwerks.com" target="_blank" rel="noopener noreferrer" underline="none">
                    <BrandImage
                        sources={reportLogoSources}
                        alt="ReportWerks"
                        sx={{
                            maxWidth: 220,
                            width: '100%',
                            maxHeight: 40,
                            objectFit: 'contain',
                            display: 'block'
                        }}
                    />
                </Link>
                <Typography variant="body2" color="text.secondary">
                    {`© ${new Date().getFullYear()} ReportWerks. All rights reserved.`}
                </Typography>
            </Stack>

        </Box>
    );

    return (
        <Box
            sx={{
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
                background: `radial-gradient(circle at top left, ${alpha(theme.palette.secondary.main, 0.22)} 0%, transparent 36%),
                    radial-gradient(circle at bottom right, ${alpha(theme.palette.primary.main, 0.24)} 0%, transparent 38%),
                    linear-gradient(135deg, #f7fafc 0%, #eef4fb 46%, #ffffff 100%)`
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.55,
                    backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)} 1px, transparent 1px),
                        linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 1px, transparent 1px)`,
                    backgroundSize: '38px 38px',
                    maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.7), transparent 90%)'
                }}
            />
            <Box
                sx={{
                    position: 'relative',
                    maxWidth: 1180,
                    mx: 'auto',
                    px: { xs: 2, sm: 3, md: 4 },
                    py: { xs: 3, sm: 4, md: 6 }
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        overflow: 'hidden',
                        borderRadius: { xs: 4, md: 6 },
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                        boxShadow: `0 30px 90px ${alpha(theme.palette.primary.main, 0.12)}`,
                        backdropFilter: 'blur(18px)',
                        background: alpha('#ffffff', 0.82),
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(360px, 0.9fr)' }
                    }}
                >
                    {loading ? (
                        <>
                            <Box
                                sx={{
                                    gridColumn: '1 / -1',
                                    px: { xs: 3, sm: 4, md: 6 },
                                    py: { xs: 5, md: 7 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    gap: 3,
                                    background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.14)} 100%)`
                                }}
                            >
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="center">
                                    <BrandImage
                                        sources={clientLogoSources}
                                        alt={clientLabel}
                                        sx={{
                                            maxHeight: 68,
                                            maxWidth: 240,
                                            width: 'auto',
                                            objectFit: 'contain'
                                        }}
                                    />
                                    <BrandImage
                                        sources={reportLogoSources}
                                        alt="ReportWerks"
                                        sx={{
                                            maxHeight: 44,
                                            maxWidth: 200,
                                            width: 'auto',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </Stack>
                                <Chip
                                    label="License verification in progress"
                                    sx={{
                                        borderRadius: 999,
                                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                                        color: theme.palette.primary.main,
                                        fontWeight: 700
                                    }}
                                />
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
                                        Checking access for {clientLabel}
                                    </Typography>
                                    <Typography sx={{ mt: 1.5, color: 'text.secondary', maxWidth: 540 }}>
                                        Loading your representative details and brand assets so this page can show the right contact information.
                                    </Typography>
                                </Box>
                                <Box sx={{ width: '100%', maxWidth: 440 }}>
                                    <LinearProgress
                                        sx={{
                                            height: 10,
                                            borderRadius: 999,
                                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: 999
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                            {renderFooter()}
                        </>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    color: '#ffffff',
                                    px: { xs: 3, sm: 4, md: 5 },
                                    py: { xs: 4, md: 5 },
                                    background: `linear-gradient(160deg, ${theme.palette.primary.dark || theme.palette.primary.main} 0%, ${theme.palette.primary.main} 48%, ${theme.palette.secondary.main} 100%)`
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -80,
                                        right: -40,
                                        width: 220,
                                        height: 220,
                                        borderRadius: '50%',
                                        bgcolor: alpha('#ffffff', 0.12)
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: -120,
                                        left: -70,
                                        width: 260,
                                        height: 260,
                                        borderRadius: '50%',
                                        bgcolor: alpha('#ffffff', 0.1)
                                    }}
                                />
                                <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                                    <Chip
                                        label="License attention required"
                                        sx={{
                                            width: 'fit-content',
                                            borderRadius: 999,
                                            bgcolor: alpha('#ffffff', 0.16),
                                            color: '#ffffff',
                                            fontWeight: 700
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: { xs: 'flex-start', sm: 'center' },
                                            justifyContent: 'space-between',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            gap: 2
                                        }}
                                    >
                                        <Box>
                                            <Typography sx={{ textTransform: 'uppercase', letterSpacing: '0.18em', fontSize: 12, opacity: 0.78 }}>
                                                {clientLabel}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    mt: 1,
                                                    fontSize: { xs: '2.2rem', md: '3.4rem' },
                                                    lineHeight: 0.98,
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.04em',
                                                    maxWidth: 540
                                                }}
                                            >
                                                Let&apos;s get this report back online.
                                            </Typography>
                                        </Box>
                                        {clientLogoSources.length ? (
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 3,
                                                    bgcolor: alpha('#ffffff', 0.12),
                                                    backdropFilter: 'blur(6px)'
                                                }}
                                            >
                                                <BrandImage
                                                    sources={clientLogoSources}
                                                    alt={clientLabel}
                                                    sx={{
                                                        maxWidth: { xs: 180, sm: 220 },
                                                        maxHeight: 72,
                                                        width: '100%',
                                                        objectFit: 'contain',
                                                        display: 'block'
                                                    }}
                                                />
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={{
                                                    minWidth: 92,
                                                    minHeight: 92,
                                                    borderRadius: 3,
                                                    px: 2,
                                                    display: 'grid',
                                                    placeItems: 'center',
                                                    bgcolor: alpha('#ffffff', 0.14),
                                                    border: `1px solid ${alpha('#ffffff', 0.18)}`
                                                }}
                                            >
                                                <Typography sx={{ fontWeight: 800, letterSpacing: '-0.04em', fontSize: '1.9rem' }}>
                                                    {clientLabel.slice(0, 2).toUpperCase()}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    <Typography sx={{ maxWidth: 620, fontSize: { xs: '1rem', md: '1.08rem' }, color: alpha('#ffffff', 0.9) }}>
                                        The dashboard for {clientName} is temporarily unavailable because the assigned license needs to be reviewed.
                                        Reach out to your representative and include the reference below so access can be restored.
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: 3,
                                                bgcolor: alpha('#ffffff', 0.12),
                                                color: '#ffffff'
                                            }}
                                        >
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <BusinessRounded />
                                                <Box>
                                                    <Typography sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.14em', opacity: 0.72 }}>
                                                        Account
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>
                                                        {clientLabel}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Paper>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: 3,
                                                bgcolor: alpha('#ffffff', 0.12),
                                                color: '#ffffff'
                                            }}
                                        >
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <BadgeRounded />
                                                <Box>
                                                    <Typography sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.14em', opacity: 0.72 }}>
                                                        License reference
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>
                                                        {licenseReference}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Paper>
                                    </Stack>
                                    {clientRecord?.agency && (
                                        <Link
                                            href={clientRecord.agency}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            sx={{ color: '#ffffff', fontWeight: 700, width: 'fit-content' }}
                                        >
                                            Visit organization website
                                        </Link>
                                    )}
                                </Stack>
                            </Box>
                            <Box
                                sx={{
                                    px: { xs: 3, sm: 4, md: 5 },
                                    py: { xs: 4, md: 5 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    gap: 3
                                }}
                            >
                                <Chip
                                    icon={<SupportAgentRounded />}
                                    label="Primary contact"
                                    sx={{
                                        width: 'fit-content',
                                        borderRadius: 999,
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        color: theme.palette.primary.main,
                                        fontWeight: 700,
                                        '& .MuiChip-icon': {
                                            color: theme.palette.primary.main
                                        }
                                    }}
                                />
                                <Stack spacing={2.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                    <Avatar
                                        src={repPhoto}
                                        alt={repName}
                                        sx={{
                                            width: 108,
                                            height: 108,
                                            border: `4px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                            boxShadow: `0 18px 38px ${alpha(theme.palette.primary.main, 0.2)}`
                                        }}
                                    />
                                    <Box sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                                        <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
                                            {repName}
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary', mt: 0.75 }}>
                                            {repRole}
                                        </Typography>
                                    </Box>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            width: '100%',
                                            p: 2.25,
                                            borderRadius: 3,
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                            bgcolor: alpha(theme.palette.primary.main, 0.03)
                                        }}
                                    >
                                        <Stack spacing={1.5}>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <AlternateEmailRounded sx={{ color: theme.palette.primary.main }} />
                                                <Box>
                                                    <Typography sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'text.secondary' }}>
                                                        Email
                                                    </Typography>
                                                    <Link href={`mailto:${repEmail}`} underline="hover" sx={{ fontWeight: 700 }}>
                                                        {repEmail}
                                                    </Link>
                                                </Box>
                                            </Stack>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <BadgeRounded sx={{ color: theme.palette.primary.main }} />
                                                <Box>
                                                    <Typography sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'text.secondary' }}>
                                                        Reference to include
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 700 }}>
                                                        {licenseReference}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        href={mailToLink}
                                        sx={{
                                            px: 3,
                                            py: 1.3,
                                            borderRadius: 999,
                                            boxShadow: 'none',
                                            textTransform: 'none',
                                            fontWeight: 700
                                        }}
                                    >
                                        Email {repName.split(' ')[0]}
                                    </Button>
                                </Stack>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 3,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                                        What to send
                                    </Typography>
                                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                                        {supportSteps.map((item, index) => (
                                            <Stack key={item} direction="row" spacing={1.5} alignItems="flex-start">
                                                <Box
                                                    sx={{
                                                        mt: 0.25,
                                                        width: 26,
                                                        height: 26,
                                                        borderRadius: '50%',
                                                        display: 'grid',
                                                        placeItems: 'center',
                                                        flexShrink: 0,
                                                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                                                        color: theme.palette.primary.main,
                                                        fontSize: 13,
                                                        fontWeight: 800
                                                    }}
                                                >
                                                    {index + 1}
                                                </Box>
                                                <Typography sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                                                    {item}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Paper>
                            </Box>
                            {renderFooter()}
                        </>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

ContactRep.propTypes = {};

export default ContactRep;
